import type {
  BadgePort,
  ClientNotification,
  ClipboardPort,
  ExternalLinkPort,
  NotificationPort,
} from '@sunucupark/client-core';

/** Diameter of the dot drawn over the favicon, and the size we rasterise it at. */
const FAVICON_SIZE = 64;
const BADGE_RADIUS = 20;

/**
 * Unread count in the document title and on the favicon. The icon is drawn on a
 * canvas from the brand's own artwork, so a customer brand keeps its mark
 * (ADR 0009); if the image cannot be drawn, the title alone carries the count.
 */
export class TitleAndFaviconBadge implements BadgePort {
  private icon: HTMLImageElement | null = null;
  private lastCount = -1;

  constructor(
    private readonly baseTitle: string,
    /** URL of the brand's favicon; same origin, so the canvas stays untainted. */
    private readonly iconUrl: string,
    /** Colour of the dot: the attention red, which stands out on any brand's mark. */
    private readonly badgeColor = '#D93F4C',
  ) {}

  set(count: number): void {
    if (count === this.lastCount) return;
    this.lastCount = count;
    document.title = count > 0 ? `(${count}) ${this.baseTitle}` : this.baseTitle;
    void this.paint(count);
  }

  private async paint(count: number): Promise<void> {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    if (count === 0) {
      link.href = this.iconUrl;
      return;
    }
    try {
      const image = await this.load();
      const canvas = document.createElement('canvas');
      canvas.width = FAVICON_SIZE;
      canvas.height = FAVICON_SIZE;
      const context = canvas.getContext('2d');
      if (!context) return;
      context.drawImage(image, 0, 0, FAVICON_SIZE, FAVICON_SIZE);
      context.beginPath();
      context.arc(FAVICON_SIZE - BADGE_RADIUS, BADGE_RADIUS, BADGE_RADIUS, 0, Math.PI * 2);
      context.fillStyle = this.badgeColor;
      context.fill();
      // A white ring parts the dot from the mark under it, whatever colour the mark is.
      context.lineWidth = 4;
      context.strokeStyle = '#FFFFFF';
      context.stroke();
      context.fillStyle = '#FFFFFF';
      context.font = `bold ${count > 9 ? 24 : 30}px system-ui, sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(count > 99 ? '99+' : String(count), FAVICON_SIZE - BADGE_RADIUS, BADGE_RADIUS + 2);
      link.href = canvas.toDataURL('image/png');
    } catch {
      // A blocked or missing icon is not worth an error; the title still shows the count.
    }
  }

  private load(): Promise<HTMLImageElement> {
    if (this.icon) return Promise.resolve(this.icon);
    return new Promise((resolve, reject) => {
      const image = new Image(FAVICON_SIZE, FAVICON_SIZE);
      image.onload = () => {
        this.icon = image;
        resolve(image);
      };
      image.onerror = () => reject(new Error('The brand icon could not be loaded'));
      image.src = this.iconUrl;
    });
  }
}

export class BrowserClipboard implements ClipboardPort {
  async writeText(text: string): Promise<void> {
    await navigator.clipboard.writeText(text);
  }

  async readText(): Promise<string | null> {
    try {
      return await navigator.clipboard.readText();
    } catch {
      // Reading the clipboard needs a permission the user may refuse.
      return null;
    }
  }
}

/** Opens links in a new tab with no handle back to us (`noopener`). */
export class BrowserExternalLink implements ExternalLinkPort {
  async open(url: string): Promise<void> {
    const target = new URL(url, window.location.href);
    if (
      target.protocol !== 'https:' &&
      target.protocol !== 'http:' &&
      target.protocol !== 'mailto:'
    ) {
      throw new Error(`Refusing to open a ${target.protocol} link`);
    }
    window.open(target.href, '_blank', 'noopener,noreferrer');
  }
}

/** Desktop notifications while a tab is open; web push comes in phase 3. */
export class BrowserNotifications implements NotificationPort {
  private readonly listeners = new Set<(target: string | undefined) => void>();

  async permission(
    options: { request?: boolean } = {},
  ): Promise<'granted' | 'denied' | 'unsupported'> {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (!options.request || Notification.permission === 'denied') return 'denied';
    return (await Notification.requestPermission()) === 'granted' ? 'granted' : 'denied';
  }

  async show(notification: ClientNotification): Promise<void> {
    if ((await this.permission()) !== 'granted') return;
    const shown = new Notification(notification.title, {
      body: notification.body,
      tag: notification.id,
    });
    shown.onclick = () => {
      window.focus();
      for (const listener of this.listeners) listener(notification.target);
      shown.close();
    };
  }

  onActivated(listener: (target: string | undefined) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
